import { useEffect, useState } from "react";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import useAlert from "../../../../hooks/useAlert";
import useAuthorizedAction, {
  AuthorizedActionsKeys,
} from "../../../../hooks/useAuthorizedAction";
import {
  UserDetailsFormInputs,
  UserStatus,
} from "../../../../models/users.interface";
import usersService from "../../../../services/users.service";
import { getFormChangedData } from "../../../../utils/formUtil";
import {
  getProUserDefaultFormPermissions,
  isEmailDomainsExist,
} from "../../../../utils/userUtil";
import AlertModal from "../../../Shared/AlertModal/AlertModal";
import CancelButton from "../../../Shared/CancelButton/CancelButton";
import Card from "../../../Shared/Card/Card";
import DateTime from "../../../Shared/DateTime/DateTime";
import NoDataIndicator from "../../../Shared/NoDataIndicator/NoDataIndicator";
import SaveButton from "../../../Shared/SaveButton/SaveButton";
import ToggleUserActiveButton from "../../EditUser/ToggleUserActiveButton/ToggleUserActiveButton";
import UserCreatedActivatedDates from "../../UserCreatedActivatedDates/UserCreatedActivatedDates";
import UserInfoInputs from "../UserInfoInputs/UserInfoInputs";
import UserPermissionInputs from "../UserPermissionInputs/UserPermissionInputs";
import styles from "./ProGeneralDetails.module.css";
import {
  ManageUsersActionType,
  useManageUsersContext,
} from "../../../../context/ManageUsersContext";
import { ErrorConstants } from "../../../../constants";
import Role from "../../../../models/role";
import { useAuthContext } from "../../../../context/AuthContext";

type ProGeneralDetailsProps = {
  userId: string;
  createdAt?: string;
  activatedAt?: string;
  loggedAt?: string;
  onUpdated?: () => void;
  onCancel?: () => void;
  onToggleUserActive?: (isActive: boolean) => void;
  formData?: UserDetailsFormInputs;
  activeProfilesCount?: number;
  totalProfiles?: number;
  userStatus?: UserStatus;
};

const ProGeneralDetails = ({
  userId,
  activatedAt,
  createdAt,
  loggedAt,
  formData,
  activeProfilesCount,
  userStatus,
  onCancel,
  onToggleUserActive,
  onUpdated,
  totalProfiles,
}: ProGeneralDetailsProps) => {
  const isCroOrg = useAuthorizedAction(AuthorizedActionsKeys.ViewAsCroOrg);
  const isCroExternOrg = useAuthorizedAction(
    AuthorizedActionsKeys.ViewAsCroExternOrg
  );

  const {
    state: { user },
  } = useAuthContext();
  const {
    state: { emailDomains, userOrgPermission },
    dispatch,
  } = useManageUsersContext();

  const showEmailDomains = isEmailDomainsExist(emailDomains);

  const [loading, setLoading] = useState(false);
  const [toggleDefaultPermissions, setToggleDefaultPermissions] = useState(
    false
  );

  const formMethods = useForm<UserDetailsFormInputs>();

  const isUserPending = userStatus === "pending";
  const isUserActive = userStatus === "active";

  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { isDirty, dirtyFields },
  } = formMethods;

  const {
    showErrorAlert,
    alertProps,
    alertMessage,
    getAlertMessageAndSupportContent,
  } = useAlert();

  const isAdmin = watch("isAdmin"); //"canManageProUsers");
  const entity = watch("entity");
  const isAdminDirty = dirtyFields["isAdmin"]; //canManageProUsers"];

  useEffect(() => {
    /**
     * This Effect resets form data once a different user is selected
     */
    if (!userId || !formData) return;

    reset(
      {
        ...formData,
        email: showEmailDomains ? formData.loginName : formData.email,
      },
      {
        keepDefaultValues: false,
        keepDirty: false,
        keepErrors: false,
        keepIsSubmitted: false,
        keepIsValid: false,
        keepSubmitCount: false,
        keepTouched: false,
        keepValues: false,
      }
    );

    setToggleDefaultPermissions(false);
  }, [userId, reset, showEmailDomains]); // eslint-disable-line

  useEffect(() => {
    /**
     * This Effect toggle default permissions for a user (Pro / Admin),
     * once a user toggles isAdmin after initial form reset
     */

    if (isAdminDirty && !toggleDefaultPermissions) {
      setToggleDefaultPermissions(true);
      return;
    }

    if (!toggleDefaultPermissions) {
      return;
    }

    const defaultProPermissions = getProUserDefaultFormPermissions(
      isAdmin ? "admin" : "pro",
      {
        isCroOrg,
        isCroExternOrg,
      }
    );

    const mergedPermissions = {
      ...defaultProPermissions,
      ...userOrgPermission.defaults,
    };

    Object.entries(mergedPermissions).forEach(([key, value]) => {
      setValue(key as keyof Partial<UserDetailsFormInputs>, value, {
        shouldDirty: true,
      });
    });
  }, [
    isAdmin,
    isAdminDirty,
    toggleDefaultPermissions,
    isCroOrg,
    isCroExternOrg,
    setValue,
    userOrgPermission.defaults,
  ]);

  useEffect(() => {
    dispatch({
      type: ManageUsersActionType.SET_ADD_USER_DETAILS,
      payload: {
        role: isAdmin ? Role.OrgAdmin : Role.OrgProfessional,
        entity,
      },
    });
  }, [isAdmin, entity, dispatch]);

  const saveHandler = async () => {
    setLoading(true);

    const changedData = getFormChangedData<UserDetailsFormInputs>(
      dirtyFields,
      getValues
    );

    if (changedData.loginName || changedData.emailDomain) {
      changedData.email = `${changedData.loginName ??
        getValues("loginName")}@${changedData.emailDomain ??
        getValues("emailDomain")}`;
    }

    try {
      const { success, errorCode } = await usersService.updateUser({
        id: userId,
        ...changedData,
      });

      if (!success) {
        handleSaveError(errorCode);
        return;
      }

      if (onUpdated) onUpdated();
    } finally {
      setLoading(false);
    }
  };

  const toggleUserActiveHandler = () => {
    if (onToggleUserActive) onToggleUserActive(!isUserActive);
  };

  const handleSaveError = (errorCode?: string) => {
    const errMsg = {
      id: "ERROR.UPDATE_USER",
      defaultText:
        "An error occurred while updating user information.<br></br>Please try again.",
    };

    if (
      errorCode === ErrorConstants.CREATE_USER_WITH_EMAIL_ALREADY_IN_USE_CODE
    ) {
      errMsg.id = "ERROR.ADD_USER.EMAIL_EXISTS";
      errMsg.defaultText =
        "This email address already exists in our network and cannot be used.";
    }

    const { message, support } = getAlertMessageAndSupportContent(
      errMsg.id,
      errMsg.defaultText,
      {
        br: () => <br></br>,
      },
      errorCode
    );
    showErrorAlert(message, support);
  };

  return (
    <div className="h-100 d-flex flex-column">
      <header className={`${styles.header} container `}>
        <div className="row d-flex flex-column py-1 mt-2 ">
          <UserCreatedActivatedDates
            activatedAt={activatedAt}
            createdAt={createdAt}
            isUserPending={isUserPending}
            className="flex-grow-1 col"
          />
        </div>
        <div className="align-items-center pb-2 row">
          <div className="col-6 text-nowrap">
            <FormattedMessage
              id="USER_DETAILS.LAST_LOGIN"
              defaultMessage="Last Login"
            />
            :<span className="mx-sm"></span>
            <b>
              {loggedAt ? <DateTime>{loggedAt}</DateTime> : <NoDataIndicator />}
            </b>
          </div>
          <div className="col-6 px-4 px-lg-0">
            <ToggleUserActiveButton
              disabled={loading}
              onClick={isUserPending ? undefined : toggleUserActiveHandler}
              isUserActive={isUserActive}
              visible={!isUserPending}
            />
          </div>
        </div>
      </header>
      <FormProvider {...formMethods}>
        <Form className="d-flex flex-column flex-grow-1">
          <div className="flex-grow-1 p-2">
            <Card className="mb-2 p-3">
              <UserInfoInputs
                isEdit
                entity={{ readonly: !!totalProfiles, restricted: "pro" }}
              />
            </Card>
            <Card className="mb-2 p-0">
              <UserPermissionInputs />
            </Card>

            <Card className="d-flex flex-row align-items-center justify-content-between p-3 mb-2 font-md">
              <PatientCount count={activeProfilesCount} active />
              <PatientCount count={totalProfiles} />
            </Card>
          </div>
          <footer className={`${styles.footer} p-3 d-flex justify-content-end`}>
            <CancelButton onClick={onCancel} disabled={loading} />

            <span className="mx-2"></span>
            <SaveButton
              onClick={handleSubmit(saveHandler)}
              className="prim-bg-gradient-color"
              disabled={!isDirty || loading}
            >
              <FormattedMessage id="GLOBAL.SAVE" defaultMessage="Save" />
              {loading && (
                <>
                  &nbsp;&nbsp;
                  <Spinner as="span" animation="border" size="sm" />
                </>
              )}
            </SaveButton>
          </footer>
        </Form>
      </FormProvider>
      <AlertModal {...alertProps}>{alertMessage}</AlertModal>
    </div>
  );
};

export default ProGeneralDetails;

type PatientCountProps = { count?: number; active?: boolean };
const PatientCount = ({ count, active }: PatientCountProps) => {
  return (
    <div className={` d-flex align-items-center`}>
      {active ? (
        <FormattedMessage
          id="USER_DETAILS.ACTIVE_PATIENTS"
          defaultMessage="Active patients"
        />
      ) : (
        <FormattedMessage
          id="USER_DETAILS.TOTAL_PATIENTS"
          defaultMessage="Total patients"
        />
      )}
      <span className="mx-1"></span>
      {!count && count !== 0 ? (
        <NoDataIndicator />
      ) : (
        <span className={`${active ? styles.activePatients : ""}`}>
          {count}
        </span>
      )}
    </div>
  );
};
